import { Link } from "react-router-dom";
import { PenTool, Timer, MousePointerClick, Award, Globe2, Puzzle, Star, Quote } from "lucide-react";
import Reveal from "../components/Reveal";
import Counter from "../components/Counter";
import PromoVideo from "../components/PromoVideo";
import Seo from "../components/Seo";
import Faq from "../components/Faq";
import { Container, Eyebrow, Cta, TextLink, SectionHead, ExportBadge } from "../components/ui-kit";
import { useReviews } from "../hooks/useReviews";
import {
  STATS, WHY_US, CATEGORIES, ABOUT_TEXT, COLOR_CHARTS, CONTACT, STUDIO_GALLERY,
} from "../mock";

const iconMap = { PenTool, Timer, MousePointerClick, Award, Globe2, Puzzle };
const P = "/images/products";
const T = "/images/thumbs";

const HERO_TILES = [
  { src: `${T}/kot_K-0021.jpg`, alt: "Kot düğme" },
  { src: `${T}/ksn_101.jpg`, alt: "KSN 101 kaplama" },
  { src: `${T}/altp_501klasik.jpg`, alt: "Çıtçıt alt parça" },
  { src: `${T}/toka_KT-003.jpg`, alt: "Toka" },
];

const Home = () => {
  const kotItems = CATEGORIES.find((c) => c.id === "kot-dugme").items.slice(0, 16);
  const rev = useReviews();

  return (
    <main data-testid="home-page">
      <Seo
        title="Koşan Düğme | Metal Tekstil Aksesuarları İmalatçısı"
        description="1993'ten beri çıtçıt, kuşgözü, perçin, kot düğme, toka, stoper ve bağ ucu üreticisi. 48 kaplama rengi, 1000+ ürün çeşidi, ihracata uygun kalite standartlarında yerli üretim."
        path="/"
      />
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-canvas" data-testid="hero-section">
        <Container className="pt-14 pb-16 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 relative z-10">
            <Reveal>
              <Eyebrow data-testid="hero-eyebrow">1993'ten beri · İstanbul</Eyebrow>
            </Reveal>
            <Reveal delay={100}>
              <h1
                className="font-display font-light text-[clamp(2.4rem,4.6vw,4.1rem)] leading-[1.06] tracking-[-0.015em] text-ink mt-6"
                data-testid="hero-title"
              >
                Metal tekstil aksesuarlarında <span className="font-medium">güvenilir üretici.</span>
              </h1>
            </Reveal>
            <Reveal delay={200}>
              <p className="text-ink2 text-[16px] sm:text-[17px] leading-relaxed max-w-xl mt-8">
                Çıtçıt, kuşgözü, perçin, kot düğme, toka, stoper ve bağ ucu. Kendi tesisimizde,
                48 kaplama rengiyle, ihracata uygun kalite standartlarında üretiyoruz.
              </p>
            </Reveal>
            <Reveal delay={300}>
              <div className="flex flex-wrap items-center gap-4 mt-10">
                <Cta to="/urunler" data-testid="hero-cta-products">Ürünleri İncele</Cta>
                <Cta to="/iletisim" variant="ghost" data-testid="hero-cta-contact">Teklif Alın</Cta>
              </div>
            </Reveal>
            <Reveal delay={400}>
              <div className="mt-14 pt-8 border-t border-line">
                <ExportBadge />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6 relative">
            <Reveal delay={150} y={20}>
              <div className="relative">
                <div className="relative aspect-[5/4] lg:aspect-[4/4.4] overflow-hidden bg-plate border border-line">
                  <img
                    src={`${P}/studio_19.jpg`}
                    alt="Kot düğme koleksiyonu"
                    className="w-full h-full object-cover hero-zoom"
                    data-testid="hero-image"
                  />
                </div>

                {/* Küçük ürün şeridi */}
                <div className="absolute -bottom-7 left-4 sm:-left-6 flex gap-1.5 bg-white p-1.5 border border-line shadow-[0_28px_60px_rgba(26,26,26,0.10)]" data-testid="hero-tiles">
                  {HERO_TILES.map((t) => (
                    <div key={t.src} className="w-16 h-16 sm:w-20 sm:h-20 bg-plate overflow-hidden">
                      <img src={t.src} alt={t.alt} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>

                {/* İstatistik kartı */}
                <div className="absolute top-5 right-5 bg-white/90 backdrop-blur-md px-5 py-4 border border-line" data-testid="hero-stat-card">
                  <p className="font-display font-light text-3xl text-ink leading-none">
                    32<span className="text-brand">+</span>
                  </p>
                  <p className="text-[10px] tracking-[0.22em] uppercase text-mute mt-2">Yıllık deneyim</p>
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* ============ SAYILAR ============ */}
      <section className="border-y border-line bg-paper" data-testid="stats-section">
        <Container className="grid grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              className={`py-9 sm:py-11 pr-6 ${i % 2 === 1 ? "pl-6 border-l border-line" : ""} ${i >= 2 ? "border-t border-line lg:border-t-0" : ""} ${i === 2 ? "lg:border-l lg:pl-6" : ""} ${i === 3 ? "lg:pl-6" : ""}`}
              data-testid={`stat-${i}`}
            >
              <p className="font-display font-light text-4xl sm:text-5xl text-ink leading-none">
                <Counter value={s.value} suffix={s.suffix} />
              </p>
              <p className="text-[11px] sm:text-[12px] text-mute mt-3 tracking-[0.18em] uppercase">{s.label}</p>
            </div>
          ))}
        </Container>
      </section>

      {/* ============ ÜRÜN GRUPLARI ============ */}
      <section className="py-24 lg:py-28 bg-canvas" data-testid="categories-section">
        <Container>
          <Reveal>
            <SectionHead
              eyebrow="Ürün Grupları"
              title="12 ana grupta 1000'den fazla ürün çeşidi"
              text="Tüm ürünlerimiz kendi tesisimizde üretilir ve renk kartelamızdaki 48 kaplamanın tamamında sunulur."
              action={<TextLink to="/urunler" data-testid="categories-all-link">Tüm ürünleri gör</TextLink>}
            />
          </Reveal>

          <div className="lattice grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 mt-14" data-testid="categories-grid">
            {CATEGORIES.map((c, i) => (
              <Reveal key={c.id} delay={(i % 6) * 60} className="h-full">
                <Link
                  to={`/urunler?kategori=${c.id}`}
                  className="group block h-full bg-paper hover:bg-white p-5 transition-colors duration-300"
                  data-testid={`category-card-${c.id}`}
                >
                  <div className="aspect-square overflow-hidden bg-plate">
                    <img
                      src={c.image}
                      alt={c.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.07]"
                    />
                  </div>
                  <div className="mt-4 flex items-start justify-between gap-2">
                    <div>
                      <h3 className="text-[12px] font-semibold tracking-[0.14em] uppercase text-ink group-hover:text-brand transition-colors">
                        {c.name}
                      </h3>
                      <p className="text-[12px] text-mute mt-1">{c.items.length} ürün</p>
                    </div>
                    <span className="w-1.5 h-1.5 rounded-full bg-line group-hover:bg-brand transition-colors mt-1.5" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* ============ KOT DÜĞME KOLEKSİYONU ============ */}
      <section className="py-24 lg:py-28 bg-paper border-y border-line" data-testid="kot-section">
        <Container className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-32">
            <Reveal>
              <Eyebrow>Öne Çıkan Seri</Eyebrow>
              <h2 className="font-display font-light text-[2rem] sm:text-[2.6rem] leading-[1.1] tracking-[-0.01em] text-ink mt-4">
                Kot düğme koleksiyonu
              </h2>
              <p className="text-ink2 leading-relaxed mt-5">
                Denim ve dış giyim için pirinç ve paslanmaz kot düğmeleri. 17 mm ve 20 mm ölçülerde,
                32 farklı model ve özel logo baskı imkânı.
              </p>
              <div className="mt-8">
                <Cta to="/urunler?kategori=kot-dugme" variant="dark" data-testid="kot-cta">32 modelin tamamı</Cta>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <div className="lattice grid grid-cols-4 sm:grid-cols-8" data-testid="kot-grid">
              {kotItems.map((it, i) => (
                <Reveal key={it.code} delay={(i % 8) * 40} className="h-full">
                  <Link
                    to="/urunler?kategori=kot-dugme"
                    className="group block bg-paper hover:bg-white p-2.5 sm:p-3 transition-colors"
                    data-testid={`kot-tile-${it.code}`}
                  >
                    <div className="aspect-square overflow-hidden bg-plate">
                      <img
                        src={it.image}
                        alt={it.name}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    </div>
                    <p className="text-center text-[10px] font-semibold tracking-[0.16em] text-mute group-hover:text-brand mt-2.5 transition-colors">
                      {it.code}
                    </p>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* ============ HAKKIMIZDA ============ */}
      <section className="py-24 lg:py-28 bg-canvas overflow-hidden" data-testid="about-section">
        <Container className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <Reveal>
              <div className="relative">
                <div className="aspect-[4/3.6] overflow-hidden bg-plate border border-line">
                  <img src={`${P}/studio_17.jpg`} alt="Çıtçıt renk seçenekleri" loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="absolute -bottom-6 right-6 bg-white border border-line px-6 py-5 shadow-[0_28px_60px_rgba(26,26,26,0.08)]">
                  <p className="font-display font-light text-4xl text-ink leading-none">1993</p>
                  <p className="text-[10px] tracking-[0.22em] uppercase text-mute mt-2">Kuruluş yılı</p>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <Reveal>
              <Eyebrow>{ABOUT_TEXT.title}</Eyebrow>
              <h2 className="font-display font-light text-[2rem] sm:text-[2.6rem] leading-[1.1] tracking-[-0.01em] text-ink mt-4">
                Modanın ve işlevselliğin <span className="font-medium">buluştuğu nokta.</span>
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <p className="text-ink2 leading-relaxed mt-6">{ABOUT_TEXT.paragraphs[0]}</p>
              <p className="text-ink2 leading-relaxed mt-4">{ABOUT_TEXT.paragraphs[2]}</p>
            </Reveal>
            <Reveal delay={220}>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-8 text-[14px] text-ink">
                {["Güngören fabrika & Merter şubesi", "İhracata uygun ürün garantisi", "Uluslararası kalite standartları", "İthalat & ihracat"].map((t) => (
                  <li key={t} className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={300}>
              <div className="mt-10">
                <TextLink to="/hakkimizda" data-testid="about-link">Bizi tanıyın</TextLink>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* ============ NEDEN BİZ ============ */}
      <section className="py-24 lg:py-28 bg-sand border-y border-line" data-testid="why-section">
        <Container>
          <Reveal>
            <SectionHead eyebrow="Neden Koşan Düğme?" title="Üretimden teslimata, her adımda yanınızdayız" />
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-12 mt-16">
            {WHY_US.map((w, i) => {
              const Icon = iconMap[w.icon];
              return (
                <Reveal key={w.title} delay={(i % 3) * 90}>
                  <div className="group border-t border-ink/15 pt-6" data-testid={`why-item-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold tracking-[0.25em] text-brand">0{i + 1}</span>
                      <Icon size={22} strokeWidth={1.4} className="text-ink2 group-hover:text-brand transition-colors" />
                    </div>
                    <h3 className="font-display font-medium text-[17px] text-ink mt-6">{w.title}</h3>
                    <p className="text-ink2 text-[14px] leading-relaxed mt-2.5">{w.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ============ RENK KARTELASI ============ */}
      <section className="py-24 lg:py-28 bg-paper" data-testid="colors-section">
        <Container className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-4">
            <Reveal>
              <Eyebrow>Kaplama Seçenekleri</Eyebrow>
              <h2 className="font-display font-light text-[2rem] sm:text-[2.6rem] leading-[1.1] tracking-[-0.01em] text-ink mt-4">
                48 farklı renk <span className="font-medium">&amp; kaplama</span>
              </h2>
              <p className="text-ink2 leading-relaxed mt-5">
                Standart, special ve ekolojik kaplama gruplarında toplam 48 renk seçeneğiyle ürünlerinizi
                koleksiyonunuza birebir uyumlu hale getiriyoruz.
              </p>
              <div className="mt-8">
                <Cta to="/renkler" variant="ghost" data-testid="colors-cta">Renk kartelasını gör</Cta>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-8 grid grid-cols-4 sm:grid-cols-6 gap-x-5 gap-y-7" data-testid="colors-preview-grid">
            {COLOR_CHARTS[0].colors.slice(0, 12).map((c, i) => (
              <Reveal key={c.code} delay={i * 40}>
                <Link to="/renkler" className="group block text-center">
                  <div className="aspect-square rounded-full overflow-hidden bg-plate ring-1 ring-line group-hover:ring-ink/30 transition-all duration-300">
                    <img src={c.image} alt={c.code} loading="lazy" className="w-full h-full object-cover scale-[1.15] group-hover:scale-125 transition-transform duration-700" />
                  </div>
                  <p className="text-[10px] font-semibold tracking-[0.18em] text-mute mt-3 group-hover:text-brand transition-colors">{c.code}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* ============ VİDEO ============ */}
      <section className="py-24 lg:py-28 bg-canvas border-t border-line" data-testid="video-section">
        <Container>
          <Reveal>
            <SectionHead
              eyebrow="Tanıtım Videosu"
              title="Üretimi yakından izleyin"
              text="Çıtçıttan kot düğmesine, tokadan stopere — ürünlerimizi ve üretim sürecimizi kısa tanıtım videomuzda keşfedin."
            />
          </Reveal>
          <Reveal delay={120} className="mt-12">
            <PromoVideo />
          </Reveal>
        </Container>
      </section>

      {/* ============ GALERİ ============ */}
      <section className="py-24 lg:py-28 bg-sand border-y border-line overflow-hidden" data-testid="studio-section">
        <Container>
          <Reveal>
            <SectionHead
              eyebrow="Yakından Bakış"
              title="Ürünlerimiz, en ince detayına kadar"
              text="Pres çıkışından kaplama banyosuna, son kontrolden paketlemeye — görseller kendi üretimimizden gerçek karelerdir."
              action={<TextLink to="/hakkimizda" data-testid="studio-gallery-link">Tüm görseller</TextLink>}
            />
          </Reveal>
        </Container>
        <div className="mt-14 flex gap-5 overflow-x-auto no-scrollbar px-5 sm:px-8 lg:px-[max(2.5rem,calc((100vw-1240px)/2+2.5rem))] pb-2" data-testid="studio-strip">
          {STUDIO_GALLERY.slice(0, 6).map((s, i) => (
            <Reveal key={s.label} delay={i * 70} className="shrink-0">
              <figure className="group w-[230px] sm:w-[270px]">
                <div className="aspect-[3/4] overflow-hidden bg-plate border border-line">
                  <img src={s.image} alt={s.label} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <figcaption className="mt-3 text-[12px] text-ink2 flex items-center gap-2">
                  <span className="text-[10px] font-semibold tracking-[0.2em] text-mute">0{i + 1}</span>
                  {s.label}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ MERTER SHOWROOM VİDEO ============ */}
      <section className="py-24 lg:py-28 bg-canvas border-y border-line overflow-hidden" data-testid="merter-showcase">
        <Container className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <Eyebrow>Merter Showroom</Eyebrow>
              <h2 className="font-display font-light text-[2rem] sm:text-[2.6rem] leading-[1.1] tracking-[-0.01em] text-ink mt-4">
                Mağazamıza <span className="font-medium">adım atın.</span>
              </h2>
              <p className="text-ink2 leading-relaxed mt-6 max-w-lg">
                Merter şubemizde binlerce ürün çeşidi raflarımızda sizi bekliyor. Numuneleri yerinde inceleyebilir,
                aradığınız modeli ve kaplamayı aynı gün seçebilirsiniz. Kısa turumuzla mağazamızı yakından keşfedin.
              </p>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-8 text-[14px] text-ink">
                {["Binlerce ürün stokta", "Yerinde numune imkânı", "Uzman satış ekibi", "Merter / Güngören"].map((t) => (
                  <li key={t} className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
              <div className="mt-9">
                <Cta to="/iletisim" variant="dark" data-testid="merter-cta">Mağaza bilgileri &amp; yol tarifi</Cta>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={150} y={20}>
              <div className="relative mx-auto max-w-[360px]">
                <div className="aspect-[9/16] overflow-hidden bg-ink border border-line shadow-[0_28px_60px_rgba(26,26,26,0.12)]">
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    poster="/images/branches/merter-poster.jpg"
                    className="w-full h-full object-cover"
                    data-testid="merter-home-video"
                  >
                    <source src="/videos/merter.mp4" type="video/mp4" />
                  </video>
                </div>
                <div className="absolute -bottom-5 -left-5 bg-white border border-line px-5 py-4 shadow-[0_20px_44px_rgba(26,26,26,0.10)]">
                  <p className="text-[10px] tracking-[0.22em] uppercase text-mute">Merter Şubesi</p>
                  <p className="font-display font-light text-xl text-ink leading-none mt-1.5">Showroom turu</p>
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* ============ MÜŞTERİ YORUMLARI ============ */}
      <section className="py-24 lg:py-28 bg-paper" data-testid="reviews-section">
        <Container className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <Reveal>
              <Eyebrow>Müşteri Yorumları</Eyebrow>
              <div className="flex items-end gap-4 mt-5">
                <p className="font-display font-light text-6xl text-ink leading-none" data-testid="home-rating">{rev.rating}</p>
                <div className="pb-1">
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={14} className="fill-brand text-brand" />
                    ))}
                  </div>
                  <p className="text-[12px] text-mute mt-1.5">Google'da {rev.review_count} değerlendirme</p>
                </div>
              </div>
              <p className="text-ink2 leading-relaxed mt-6">
                Çalıştığımız marka ve müşterilerin deneyimlerini Google üzerinden takip edebilirsiniz.
              </p>
              <div className="mt-8">
                <TextLink to="/iletisim" data-testid="reviews-link">Tüm yorumlar</TextLink>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-3 gap-5">
            {rev.reviews.slice(0, 3).map((r, i) => (
              <Reveal key={`${r.author}-${i}`} delay={i * 100} className="h-full">
                <div className="h-full border border-line bg-canvas p-6 flex flex-col" data-testid={`home-review-${i}`}>
                  <Quote size={20} className="text-brand/60" />
                  <p className="text-ink2 text-[14px] leading-relaxed mt-4 flex-1">{r.text}</p>
                  <div className="flex items-center gap-3 mt-6 pt-5 border-t border-line">
                    {r.profile_photo ? (
                      <img src={r.profile_photo} alt={r.author} className="w-9 h-9 rounded-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <span className="w-9 h-9 rounded-full bg-ink text-white text-[13px] font-semibold flex items-center justify-center">{r.initial}</span>
                    )}
                    <div>
                      <p className="text-[13px] font-semibold text-ink">{r.author}</p>
                      <p className="text-[11px] text-mute">{r.relative_time}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* ============ SSS ============ */}
      <Faq />

      {/* ============ CTA ============ */}
      <section className="pb-24 lg:pb-28 bg-paper" data-testid="cta-section">
        <Container>
          <Reveal>
            <div className="relative overflow-hidden bg-ink text-white px-8 py-14 sm:px-14 sm:py-20 grid lg:grid-cols-12 gap-10 items-center">
              <div className="absolute -right-24 -top-24 w-[420px] h-[420px] rounded-full border border-white/10" />
              <div className="absolute -right-10 -bottom-40 w-[420px] h-[420px] rounded-full border border-white/10" />
              <div className="lg:col-span-7 relative">
                <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-white/60">Teklif &amp; Numune</p>
                <h2 className="font-display font-light text-3xl sm:text-5xl leading-[1.08] tracking-[-0.015em] mt-5">
                  Projeniz için bugün <span className="font-medium">teklif alın.</span>
                </h2>
                <p className="text-white/65 leading-relaxed mt-5 max-w-lg">
                  Ürün, ölçü ve kaplama tercihlerinizi iletin; numune ve fiyat çalışmasını aynı gün başlatalım.
                </p>
              </div>
              <div className="lg:col-span-5 relative flex flex-col sm:flex-row lg:flex-col gap-4 lg:items-end">
                <Cta to="/iletisim" data-testid="cta-contact">İletişim formu</Cta>
                <a
                  href={CONTACT.phoneHref}
                  className="inline-flex items-center justify-center h-12 px-7 rounded-full border border-white/30 hover:border-white text-[13px] font-semibold tracking-[0.04em] transition-colors"
                  data-testid="cta-phone"
                >
                  {CONTACT.phone}
                </a>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>
    </main>
  );
};

export default Home;
